import {APP_API_URL} from '#/lib/constants'
import {type WsocError, type WsocFetchOptions} from './types'

/** Typed fetch error with HTTP status and XRPC error code */
export class WsocFetchError extends Error {
  readonly status: number
  readonly code: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'WsocFetchError'
    this.status = status
    this.code = code ?? message
  }
}

/**
 * Central fetch helper for all W Social API calls.
 *
 * - Builds full URL from APP_API_URL when path does not start with "http"
 * - Sets Content-Type and Authorization headers automatically
 * - Parses error body and throws WsocFetchError on !response.ok
 * - Handles empty response bodies (e.g. 204 No Content)
 */
export async function wsocFetch<T>(
  path: string,
  options: WsocFetchOptions = {},
): Promise<T> {
  const {method = 'POST', body, accessJwt, signal} = options

  const isPost = method === 'POST'
  // const resolvedBody= body ?? (isPost ? {} : undefined)

  const headers: Record<string, string> = {}
  if (body !== undefined || isPost) {
    headers['Content-Type'] = 'application/json'
  }
  if (accessJwt) {
    headers.Authorization = `Bearer ${accessJwt}`
  }

  const url = path.startsWith('http') ? path : `${APP_API_URL}${path}`

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (!response.ok) {
    const errBody = (await response
      .json()
      .catch(() => ({}))) as Partial<WsocError>
    const message = errBody.message ?? errBody.error ?? response.statusText
    throw new WsocFetchError(message, response.status, errBody.error)
  }

  const text = await response.text()
  if (!text) {
    return {} as T
  }

  return JSON.parse(text) as T
}

/** Build an XRPC path: `/xrpc/<endpoint>` */
export function xrpcPath(endpoint: string): string {
  return `/xrpc/${endpoint}`
}
